/**
 * SystemHealthBadge — live badge in the admin navbar showing overall
 * infrastructure state (System Health · aggiornato 3 min fa).
 *
 * Fabrizio CR 01/07/2026 — Enhancement over the daily email cron:
 * gives super_admin + IT users a real-time "traffic light" that
 * updates every 5 min without opening the mailbox. Clicks through
 * to the full System Health page (super_admin) or shows a tooltip
 * (IT users, who don't have access to the detailed page).
 *
 * Backend: ``GET /api/admin/system/health-checks/summary`` (accessible
 * to any authenticated admin — returns only counts, no credential
 * status). If the endpoint 401/403s, the badge silently hides itself
 * (defensive: never breaks the navbar).
 *
 * Polling: 5-min interval + immediate refresh on window focus. The
 * initial fetch is delayed by 800ms to avoid piling on top of the
 * dashboard's other startup requests.
 */
import { useEffect, useState, useCallback, useContext, useRef } from "react";
import { toast } from "sonner";
import { Activity } from "lucide-react";
import { API } from "../../config";
// Import the context directly (not the useAuth hook) so we can read
// AuthContext safely without throwing when the widget is mounted
// outside an AuthProvider (e.g. inside the IT Portal, which has its
// own token store in ``localStorage.it_token``).
import { AuthContext } from "../../context/AuthContext";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const INITIAL_DELAY_MS = 800;

// Severity ordering used by the transition detector. A status that
// climbs to a HIGHER severity fires an in-app toast (and, if allowed,
// a native browser notification). ``unknown`` never triggers alerts.
const SEVERITY = { ok: 0, warn: 1, error: 2 };

// LocalStorage key for cross-tab dedup of "last notified status".
// Namespaced by user email so two admins on the same device don't
// silence each other's alerts.
const LAST_NOTIFIED_KEY = (email) => `lojack_health_badge_last_notified:${email || "anon"}`;

// Env-detection on the frontend: don't nag with toasts in Preview
// where warn/error is the expected steady state (missing .p12,
// empty Salesforce creds, no LibreOffice). Matches backend
// ``services/environment.py::is_preview()`` heuristic.
const isPreviewEnv = () => {
  if (typeof window === "undefined") return false;
  return window.location.host.includes(".preview.emergentagent.com");
};

// Status-driven visual language. Kept inline so the widget is a
// single portable file that can drop into any navbar.
const STATUS_STYLE = {
  ok:    { color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)", label: "OK",     glyph: "🟢" },
  warn:  { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.35)", label: "WARN",   glyph: "🟡" },
  error: { color: "#ef4444", bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.35)",  label: "ERROR",  glyph: "🔴" },
  unknown:{color: "#6b7280", bg: "rgba(107,114,128,0.10)", border: "rgba(107,114,128,0.30)", label: "…",    glyph: "⚪" },
};

const relativeMinutes = (isoString) => {
  if (!isoString) return "mai";
  const then = new Date(isoString).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return "adesso";
  if (diffMin === 1) return "1 min fa";
  if (diffMin < 60) return `${diffMin} min fa`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH === 1) return "1 ora fa";
  return `${diffH} ore fa`;
};

/**
 * @param {object} props
 * @param {boolean} [props.clickable] — click badge to open /admin/system (super_admin only)
 * @param {boolean} [props.compact]   — hide the "N min fa" tail (mobile / narrow nav)
 * @param {object}  [props.authHeaderOverride] — {Authorization: 'Bearer …'} for
 *     contexts that don't use AuthContext (e.g. IT Portal has its own token
 *     stored in ``localStorage.it_token``). When provided, bypasses useAuth().
 * @param {string}  [props.roleOverride] — user role when authHeaderOverride is used.
 *     Controls whether the badge is clickable (super_admin only).
 */
export const SystemHealthBadge = ({
  clickable = true,
  compact = false,
  authHeaderOverride = null,
  roleOverride = null,
}) => {
  const authCtx = useContext(AuthContext);
  // Prefer explicit overrides (IT Portal path); fall back to AuthContext
  // (Admin Dashboard path). This lets the same component live in both
  // navbars without a hard dependency on AuthContext.
  const authHeader = authHeaderOverride || (authCtx?.getAuthHeader ? authCtx.getAuthHeader() : null);
  const role = roleOverride || authCtx?.user?.role || null;
  const userEmail = authCtx?.user?.email || null;
  const isAuthed = !!authHeader;

  const [state, setState] = useState({ status: "unknown", summary: null, ts: null, loading: true, hidden: false });
  // Remember the previous status so we can detect UP→WARN, UP→ERROR,
  // and WARN→ERROR transitions and fire an in-app notification
  // exactly once per severity climb. Using ``useRef`` (not state)
  // so the transition detector runs synchronously and never causes
  // an extra render.
  const prevStatusRef = useRef(null);
  // Tick counter to force re-render of the "3 min fa" label every 30s
  // without hitting the backend. Isolated in its own effect so it doesn't
  // interfere with the polling interval.
  const [, setTick] = useState(0);

  // Fire an in-app toast (and, if allowed, a browser Notification) when
  // the overall status climbs to a higher severity than the last one
  // we already notified for. De-duped via ``localStorage`` per user so
  // the same alert doesn't fire again on every tab reload. Silent in
  // Preview because warn/error is the intentional steady state there.
  const notifyOnClimb = useCallback((nextStatus, summary) => {
    if (isPreviewEnv()) return;
    const nextSev = SEVERITY[nextStatus];
    if (nextSev === undefined || nextSev === 0) return; // only warn/error trigger

    const storageKey = LAST_NOTIFIED_KEY(userEmail);
    let lastNotified = null;
    try {
      lastNotified = JSON.parse(localStorage.getItem(storageKey) || "null");
    } catch { /* ignore */ }
    const lastSev = lastNotified ? SEVERITY[lastNotified.status] : -1;

    // Only notify when severity actually WORSENS since the last toast.
    // A repeated ``warn`` won't fire a second toast; only warn→error will.
    if (nextSev <= lastSev && prevStatusRef.current) return;

    const title = nextStatus === "error"
      ? "System Health: ERROR"
      : "System Health: WARN";
    const description = summary
      ? `${summary.error} error · ${summary.warn} warn · ${summary.ok} ok · dettagli in Admin Dashboard`
      : "Verifica lo stato del sistema";

    // In-app toast — always shown (independent of browser Notification permission).
    // ``sonner`` respects its own DedupeKey via the ``id`` prop.
    toast[nextStatus === "error" ? "error" : "warning"](title, {
      description,
      duration: nextStatus === "error" ? 15000 : 10000,
      id: `system-health-${nextStatus}`, // sonner dedup within the tab
      action: role === "super_admin" ? {
        label: "Apri",
        onClick: () => { window.location.href = "/admin/system/history"; },
      } : undefined,
    });

    // Optional: native browser Notification for extra visibility even
    // when the admin has this tab in the background. Requires the user
    // to have granted permission at least once — we do NOT auto-prompt
    // because that's aggressive UX; a super_admin can opt in via a
    // one-off click on the badge (see click handler below).
    try {
      if (
        typeof window !== "undefined" &&
        "Notification" in window &&
        window.Notification.permission === "granted"
      ) {
        // Fire-and-forget; we don't need a handle to the notification.
        new window.Notification(title, {
          body: description,
          icon: "/lojack-icon-192.png",
          tag: "lojack-system-health", // replaces previous notification
          silent: false,
        });
      }
    } catch { /* ignore */ }

    try {
      localStorage.setItem(storageKey, JSON.stringify({
        status: nextStatus,
        ts: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
  }, [userEmail, role]);

  const fetchStatus = useCallback(async () => {
    if (!isAuthed) return;
    try {
      // Native ``fetch`` (not axios) to bypass any global interceptors
      // that may otherwise clobber our Authorization header. The badge
      // needs to survive both AuthContext-wrapped pages (Admin) and
      // standalone pages that use their own token store (IT Portal).
      const auth = authHeader?.headers?.Authorization || authHeader?.Authorization;
      const r = await fetch(`${API}/admin/system/health-checks/summary`, {
        headers: auth ? { Authorization: auth } : {},
      });
      if (!r.ok) {
        // 401 → session expired: keep polling, next call may catch a
        //        fresh token. Show the badge in "unknown" state.
        // 403 → role isn't allowed: hide silently.
        // Other errors (5xx, network): keep last known status.
        if (r.status === 403) {
          setState(s => ({ ...s, loading: false, hidden: true }));
        } else if (r.status === 401) {
          setState(s => ({ ...s, loading: false, status: "unknown" }));
        } else {
          setState(s => ({ ...s, loading: false }));
        }
        return;
      }
      const data = await r.json();
      const nextStatus = data.overall_status || "unknown";
      // Compare against previous status BEFORE updating state so we
      // never trigger a toast on the initial load — only on genuine
      // transitions during the session.
      if (prevStatusRef.current !== null && prevStatusRef.current !== nextStatus) {
        notifyOnClimb(nextStatus, data.summary || null);
      } else if (prevStatusRef.current === null) {
        // First load: still call notifier so it can fire when the
        // page opens directly into a warn/error state (and the last
        // notified severity from localStorage is lower).
        notifyOnClimb(nextStatus, data.summary || null);
      }
      prevStatusRef.current = nextStatus;
      setState({
        status: nextStatus,
        summary: data.summary || null,
        ts: data.ts || new Date().toISOString(),
        loading: false,
        hidden: false,
      });
    } catch (e) {
      // Network error, DNS failure, etc. — keep last known status.
      setState(s => ({ ...s, loading: false }));
    }
  }, [isAuthed, authHeader, notifyOnClimb]);

  useEffect(() => {
    if (!isAuthed) return;
    const initial = setTimeout(fetchStatus, INITIAL_DELAY_MS);
    const iv = setInterval(fetchStatus, POLL_INTERVAL_MS);
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    return () => {
      clearTimeout(initial);
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthed, fetchStatus]);

  // Re-render the "N min fa" label every 30s so it stays truthful
  // without hammering the backend.
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  if (state.hidden || !isAuthed) return null;

  const style = STATUS_STYLE[state.status] || STATUS_STYLE.unknown;
  const isSuperAdmin = role === "super_admin";

  // Click handler: 
  //   • Shift-click → toggle native Notification permission (opt-in). We
  //     never auto-prompt because that's aggressive UX; the badge itself
  //     is the affordance.
  //   • Normal click → navigate to /admin/system (super_admin only).
  const goTo = (e) => {
    if (e?.shiftKey && typeof window !== "undefined" && "Notification" in window) {
      if (window.Notification.permission === "default") {
        window.Notification.requestPermission().then((perm) => {
          if (perm === "granted") {
            toast.success("Notifiche browser attivate", {
              description: "Riceverai un pop-up quando il sistema entra in stato WARN o ERROR.",
              duration: 6000,
            });
          } else {
            toast.info("Notifiche non attivate", {
              description: "Le riceverai comunque come toast qui in-app.",
              duration: 5000,
            });
          }
        });
      } else if (window.Notification.permission === "granted") {
        toast.info("Notifiche browser: attive", {
          description: "Disattivale dalle impostazioni del browser (icona lucchetto in URL).",
          duration: 6000,
        });
      } else {
        toast.info("Notifiche browser: bloccate dal browser", {
          description: "Sblocca dalle impostazioni sito (icona lucchetto in URL) e ricarica.",
          duration: 7000,
        });
      }
      return;
    }
    if (clickable && isSuperAdmin) window.location.href = "/admin/system/history";
  };

  const notifPermission = typeof window !== "undefined" && "Notification" in window
    ? window.Notification.permission
    : "unsupported";
  const notifHint = notifPermission === "granted"
    ? " · notifiche browser ON"
    : notifPermission === "default" && isSuperAdmin
      ? " · shift+click per attivare notifiche browser"
      : "";

  const tooltip = state.summary
    ? `Sistema: ${style.label} · ${state.summary.ok} ok · ${state.summary.warn} warn · ${state.summary.error} error · aggiornato ${relativeMinutes(state.ts)}${notifHint}`
    : "System Health — caricamento…";

  return (
    <button
      type="button"
      onClick={goTo}
      disabled={!clickable || !isSuperAdmin}
      title={tooltip}
      aria-label={tooltip}
      data-testid="system-health-badge"
      data-status={state.status}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-all border ${
        clickable && isSuperAdmin ? "hover:brightness-110 cursor-pointer" : "cursor-default"
      }`}
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
      }}
    >
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{
            background: style.color,
            boxShadow: state.status === "error" ? `0 0 8px ${style.color}` : "none",
            animation: state.status === "error" ? "pulse 1.5s ease-in-out infinite" : "none",
          }}
        />
        {!compact && <Activity size={12} strokeWidth={2.5} />}
        <span className="tracking-wide">System</span>
        <span className="font-semibold">{style.label}</span>
      </span>
      {!compact && state.ts && (
        <span className="text-[10px] opacity-70 border-l border-current pl-2 ml-1">
          {relativeMinutes(state.ts)}
        </span>
      )}
    </button>
  );
};

export default SystemHealthBadge;
